import { apiClient } from '@/shared/lib/axios';
import type {
  RezeptLimit,
  RezeptLimitFormData,
  RezeptLimitListQuery,
  RezeptLimitListResponse,
} from '@/types/admin-rezept-limit';

type ApiEnvelope<T = unknown> = { success?: boolean; message?: string; data: T };

/**
 * Fetch paginated list of rezept limits for admin.
 */
export const getRezeptLimitList = async (
  siteType: string,
  query?: RezeptLimitListQuery
): Promise<RezeptLimitListResponse> => {
  const response = await apiClient.get<ApiEnvelope<RezeptLimitListResponse>>(
    `/admin/${siteType}/rezept-limits`,
    { params: query }
  );
  return response.data.data;
};

/**
 * Create a new rezept limit.
 */
export const createRezeptLimit = async (
  siteType: string,
  data: RezeptLimitFormData
): Promise<RezeptLimit> => {
  const response = await apiClient.post<ApiEnvelope<RezeptLimit>>(
    `/admin/${siteType}/rezept-limits`,
    data
  );
  return response.data.data;
};

/**
 * Bulk create rezept limits for a selected year.
 */
export const bulkCreateRezeptLimits = async (
  siteType: string,
  year: number
): Promise<{ created_count: number; skipped_count: number; already_exists: boolean }> => {
  const response = await apiClient.post<ApiEnvelope<{ created_count: number; skipped_count: number; already_exists: boolean }>>(
    `/admin/${siteType}/rezept-limits/bulk-create`,
    { year }
  );
  return response.data.data;
};

/**
 * Get a single rezept limit by ID.
 */
export const getRezeptLimitDetail = async (
  siteType: string,
  id: number
): Promise<RezeptLimit> => {
  const response = await apiClient.get<ApiEnvelope<RezeptLimit>>(
    `/admin/${siteType}/rezept-limits/${id}`
  );
  return response.data.data;
};

/**
 * Update an existing rezept limit.
 */
export const updateRezeptLimit = async (
  siteType: string,
  id: number,
  data: RezeptLimitFormData
): Promise<RezeptLimit> => {
  const response = await apiClient.put<ApiEnvelope<RezeptLimit>>(
    `/admin/${siteType}/rezept-limits/${id}`,
    data
  );
  return response.data.data;
};

/**
 * Download rezept limits as CSV (blob).
 */
export const downloadRezeptLimitsCsv = async (
  siteType: string,
  query?: RezeptLimitListQuery
): Promise<void> => {
  const response = await apiClient.get(`/admin/${siteType}/rezept-limits/csv`, {
    params: query,
    responseType: 'blob',
  });

  const blob = response.data;
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `rezept_limit${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};
